import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx"

import type { ILock } from "@/domains/lock"
import type { SelectedLockOrchestrator } from "@/domains/lock-page/selected-lock-orchestrator"
import type { WithdrawOrchestrator } from "@/domains/lock-page/withdraw-orchestrator"
import type { IWithdrawLock } from "@/domains/lock-page/withdraw-store"
import type { ILockRepository } from "@/domains/lock/interfaces"
import { IStorageBackend, IUrlParams } from "@/domains/common/storage"
import { SortDirection, SortType } from "@/domains/table-processor"

// Type for sortBy object structure (same as LiquidityPageStore)
export interface SortBy {
  key: string
  direction: SortDirection
  type: SortType
}

// SelectedNfts param sources
export interface SelectedNftsParamSources {
  urlParam: number[] | undefined
  localstorage: number[] | undefined
  default: number[]
  ui: number[] | undefined
}

// ListView param sources
export interface ListViewParamSources {
  urlParam: boolean | undefined
  localstorage: boolean | undefined
  default: boolean
  ui: boolean | undefined
}

// SortBy param sources (same as LiquidityPageStore)
export interface SortByParamSources {
  urlParam: SortBy | undefined
  localstorage: SortBy | undefined
  default: SortBy
  ui: SortBy | undefined
}

function convertLockStoreToIWithdrawLock(lock: {
  id: number
  lockAmount: number
  hasExpired: boolean
  needsResetToWithdraw: boolean
  canWithdraw: boolean
  displayLockEnds: string
  displayLockAmount: string
}): IWithdrawLock {
  return {
    id: lock.id,
    lockAmount: lock.lockAmount,
    hasExpired: lock.hasExpired,
    needsResetToWithdraw: lock.needsResetToWithdraw,
    canWithdraw: lock.canWithdraw,
    displayLockEnds: lock.displayLockEnds,
    displayLockAmount: lock.displayLockAmount,
  }
}

export class LockPageStore {
  WithdrawOrchestrator: WithdrawOrchestrator
  SelectedLockOrchestrator: SelectedLockOrchestrator

  selectedLockId: number | null = null

  selectedNftsParamSources: SelectedNftsParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: [],
    ui: undefined,
  }

  listViewParamSources: ListViewParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: true,
    ui: undefined,
  }

  sortByParamSources: SortByParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: { key: "id", direction: "desc", type: "number" },
    ui: undefined,
  }

  constructor(
    private storage: IStorageBackend,
    private urlParams: IUrlParams,
    private chainSlug: string,
    withdrawOrchestrator: WithdrawOrchestrator,
    selectedLockOrchestrator: SelectedLockOrchestrator,
    private lockRepository: ILockRepository,
  ) {
    this.WithdrawOrchestrator = withdrawOrchestrator
    this.SelectedLockOrchestrator = selectedLockOrchestrator

    makeObservable(this, {
      WithdrawOrchestrator: observable,
      SelectedLockOrchestrator: observable,
      selectedLockId: observable,
      selectedNftsParamSources: observable,
      listViewParamSources: observable,
      sortByParamSources: observable,
      selectedLock: computed,
      selectedNfts: computed,
      listView: computed,
      sortBy: computed,
      setSelectedLockId: action,
      setSelectedNftsFromUi: action,
      setListViewFromUrlParam: action,
      setListViewFromLocalStorage: action,
      setListViewFromUi: action,
      setSortByFromUrlParam: action,
      setSortByFromLocalStorage: action,
      setSortByFromUi: action,
    })

    // Give SelectedLockOrchestrator callbacks to update and read selectedLockId
    this.SelectedLockOrchestrator.setUpdateSelectedLockIdCallback(
      (lockId) => {
        this.setSelectedLockId(lockId)
      },
      () => {
        return this.selectedLockId
      },
    )

    // Update withdraw orchestrator when selectedLock changes
    reaction(
      () => this.selectedLock,
      (selectedLock) => {
        // Convert LockStore to IWithdrawLock for withdraw orchestrator
        const withdrawLock: IWithdrawLock | null = selectedLock
          ? convertLockStoreToIWithdrawLock({
              id: selectedLock.id,
              lockAmount: selectedLock.lockAmount,
              hasExpired: selectedLock.hasExpired,
              needsResetToWithdraw: selectedLock.needsResetToWithdraw,
              canWithdraw: selectedLock.canWithdraw,
              displayLockEnds: selectedLock.displayLockEnds,
              displayLockAmount: selectedLock.displayLockAmount,
            })
          : null
        this.WithdrawOrchestrator.updateLock(withdrawLock)
      },
    )
  }

  initialize(): void {
    runInAction(() => {
      this.initializeFromStorage()
      this.initializeFromUrlParams()
    })
  }

  private initializeFromStorage(): void {
    // Note: selectedNfts are NOT persisted to localStorage or URL params
    // This prevents stale selections from persisting across sessions
    // Selections should only exist in UI state during the current session

    // Initialize listView from localStorage
    const localListView = this.storage.getItem(`${this.chainSlug}-lock-listView`)
    if (localListView) {
      const parsedListView = localListView === "true"
      this.setListViewFromLocalStorage(parsedListView)
    }

    // Initialize sortBy from localStorage
    const localSortBy = this.storage.getItem(`${this.chainSlug}-lock-sortBy`)
    if (localSortBy) {
      try {
        const parsedSortBy = JSON.parse(localSortBy) as SortBy
        this.setSortByFromLocalStorage(parsedSortBy)
      } catch {
        // Invalid JSON, ignore
      }
    }
  }

  public initializeFromUrlParams(): void {
    // Note: selectedNfts are NOT read from URL params - they should only exist in UI state
    // This prevents stale selections from persisting across page loads
    // Clean up any existing selectedNfts URL param
    if (this.urlParams.getParam("selectedNfts")) {
      this.urlParams.removeParam("selectedNfts")
    }

    // Initialize listView from URL params
    const urlListView = this.urlParams.getParam("listView")
    if (urlListView) {
      const parsedListView = urlListView === "true"
      this.setListViewFromUrlParam(parsedListView)
    }

    // Initialize sortBy from URL params (combining sortKey, sortDir, sortType)
    const urlSortKey = this.urlParams.getParam("sortKey")
    const urlSortDir = this.urlParams.getParam("sortDir")
    const urlSortType = this.urlParams.getParam("sortType")

    if (urlSortKey && urlSortDir && urlSortType) {
      const sortBy: SortBy = {
        key: urlSortKey,
        direction: urlSortDir as SortDirection,
        type: urlSortType as SortType,
      }
      this.setSortByFromUrlParam(sortBy)
    }
  }

  // Computed getters with priority: ui > urlParam > localstorage > default
  get selectedLock(): ILock | null {
    if (this.selectedLockId === null) {
      return null
    }

    const lockStore = this.lockRepository.find(this.selectedLockId)
    if (!lockStore) {
      return null
    }

    return lockStore
  }

  setSelectedLockId(lockId: number | null): void {
    this.selectedLockId = lockId
  }

  get selectedNfts(): number[] {
    // Priority: ui > default (URL params and localStorage removed to prevent stale selections)
    return (
      this.selectedNftsParamSources.ui ??
      this.selectedNftsParamSources.default
    )
  }

  get listView(): boolean {
    return (
      this.listViewParamSources.ui ??
      this.listViewParamSources.urlParam ??
      this.listViewParamSources.localstorage ??
      this.listViewParamSources.default
    )
  }

  get sortBy(): SortBy {
    return (
      this.sortByParamSources.ui ??
      this.sortByParamSources.urlParam ??
      this.sortByParamSources.localstorage ??
      this.sortByParamSources.default
    )
  }

  // SelectedNfts setters
  setSelectedNftsFromUi(value: number[] | undefined): void {
    if (value === undefined) return
    if (JSON.stringify(this.selectedNftsParamSources.ui) === JSON.stringify(value)) return
    this.selectedNftsParamSources.ui = value

    // Note: URL params are NOT used for selectedNfts to prevent stale selections from persisting
    // Selections should only exist in UI state during the current session
  }

  // ListView setters
  setListViewFromUrlParam(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.listViewParamSources.urlParam === value) return
    this.listViewParamSources.urlParam = value
  }

  setListViewFromLocalStorage(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.listViewParamSources.localstorage === value) return
    this.listViewParamSources.localstorage = value
  }

  setListViewFromUi(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.listViewParamSources.ui === value) return
    this.listViewParamSources.ui = value

    // Auto-sync to storage
    this.storage.setItem(`${this.chainSlug}-lock-listView`, value.toString())
    this.urlParams.setParam("listView", value.toString())
  }

  // SortBy setters (same pattern as LiquidityPageStore)
  setSortByFromUrlParam(value: SortBy | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.urlParam?.key === value.key &&
      this.sortByParamSources.urlParam?.direction === value.direction &&
      this.sortByParamSources.urlParam?.type === value.type
    )
      return
    this.sortByParamSources.urlParam = value
  }

  setSortByFromLocalStorage(value: SortBy | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.localstorage?.key === value.key &&
      this.sortByParamSources.localstorage?.direction === value.direction &&
      this.sortByParamSources.localstorage?.type === value.type
    )
      return
    this.sortByParamSources.localstorage = value
  }

  setSortByFromUi(value: SortBy | undefined): void {
    if (!value) return

    if (
      this.sortByParamSources.ui?.key === value.key &&
      this.sortByParamSources.ui?.direction === value.direction &&
      this.sortByParamSources.ui?.type === value.type
    )
      return
    this.sortByParamSources.ui = value

    // Auto-sync to storage (spread sortBy into individual URL params)
    this.storage.setItem(`${this.chainSlug}-lock-sortBy`, JSON.stringify(value))
    this.urlParams.setParam("sortKey", value.key)
    this.urlParams.setParam("sortDir", value.direction)
    this.urlParams.setParam("sortType", value.type)
  }

  // Convenience methods (similar to LockManager's business logic)
  toggleNftSelection(nftId: number): void {
    const selectedNfts = this.selectedNfts

    if (selectedNfts.includes(nftId)) {
      this.setSelectedNftsFromUi(selectedNfts.filter((id) => id !== nftId))
    } else {
      const sortedIds = [...selectedNfts, nftId]
      this.setSelectedNftsFromUi(sortedIds)
    }
  }

  clearSelectedNfts(): void {
    this.setSelectedNftsFromUi([])
  }

  setSelectedNfts(value: number[]): void {
    this.setSelectedNftsFromUi(value)
  }

  getCanSelectAll(totalNfts: number): boolean {
    const numberSelected = this.selectedNfts.length
    return numberSelected !== totalNfts
  }

  getCanDeselectAll(): boolean {
    const numberSelected = this.selectedNfts.length
    return numberSelected > 0
  }

  getCanShowPortal(): boolean {
    const numberSelected = this.selectedNfts.length
    const listView = this.listView
    return numberSelected > 0 && listView
  }
}
